import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { fetchDepartments } from '../../APIServices/DepartmentsServices';
import complaintService from '../../services/complaintService';
import { useLocations } from '../../contexts/LocationProvider';
import authService from '../../services/authService';
import TranslatedText from '../TranslatedText';

function AssignComplaintPage({ complaint, onClose, onAssignSuccess }) {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  // Helper function to convert numbers to Devanagari
  const toDevanagariDigits = (num) => {
    if (num === undefined || num === null || num === 'NA') return num;
    const devanagariDigits = ['०', '१', '२', '३', '४', '५', '६', '७', '८', '९'];
    return String(num).replace(/\d/g, (digit) => devanagariDigits[parseInt(digit)]);
  };

  const formatNumberForLanguage = (num, lang) => {
    if (lang === 'mr') {
      return toDevanagariDigits(num);
    }
    return num;
  };
  const [assignee, setAssignee] = useState('');
  const [officer, setOfficer] = useState('');
  const [note, setNote] = useState('');
  const { departments, loading } = useLocations();
  const [officers, setOfficers] = useState([]);
  const [errors, setErrors] = useState({});


  const handleAssign = async () => {
    // Validate Department
    if (!assignee) {
      setErrors({ department: 'Please assign a department.' });
      return;
    }

    const assignData = {
      departmentId: parseInt(assignee),
      reason: note || 'Assigned by admin',
      priority: complaint.priority, // map existing complaint priority
    };





    // const newAssignData = {
    //   "departmentId": assignData.departmentId,
    //   "reason": assignData.reason}


    try {
      await complaintService.assignComplaint(complaint.id, assignData);

      // Call the success handler to refresh dashboard data
      if (onAssignSuccess) {
        await onAssignSuccess();
      } else {
        onClose();
      }
    } catch (error) {
      console.error('Failed to assign complaint:', error);
      alert('Failed to assign complaint. Please try again.');
    }
  };

  const user = authService.getCurrentUser();
  const departmentName = user?.departmentName;


  // Fetch officers
  const loadOfficers = async () => {
    try {
      const data = await complaintService.getOfficers();
      setOfficers(data);
    } catch (error) {
      console.error('Failed to fetch officers', error);
    }
  };

  return (
    <div className="fixed inset-0 z-50 w-full h-full overflow-y-auto bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="relative top-4 mx-auto w-11/12 max-w-5xl bg-white rounded-lg shadow-2xl mb-8 md:top-4 md:w-11/12 sm:top-0 sm:w-full sm:h-full sm:rounded-none sm:mb-0 overflow-y-auto max-h-[90vh]">
        {/* Professional Header */}
        <div className="px-4 py-4 border-b border-gray-200 rounded-t-lg bg-gray-50 sm:rounded-t-none md:px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">
                  {t('nav.dashboard')}
                </h1>
                <p className="text-sm text-gray-500">   {t('assign.title')}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {/* <span className="text-sm text-gray-500">Assignment</span> */}
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Complaint Header */}
        <div className="px-4 py-4 border-b border-gray-200 md:px-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-gray-900">
                {t('assign.title')}: {complaint.complaintId}
              </h2>
              <div className="flex items-center mt-2 space-x-4">
                <span className="text-sm text-gray-500">
                  {formatNumberForLanguage(complaint.submittedDate, currentLanguage)}
                </span>
                <span className="text-sm text-gray-500">•</span>
                <span className="text-sm text-gray-500"><TranslatedText>{complaint.name}</TranslatedText></span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Layout */}
        <div className="flex flex-col bg-gray-50 lg:flex-row">
          {/* Left Content - Complainant Details */}
          <div className="w-full p-4 bg-white border-b border-r-0 border-gray-200 md:p-6 lg:w-80 lg:border-b-0 lg:border-r">
            <div className="mb-6 text-center">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-3 bg-blue-100 rounded-full">
                <svg
                  className="w-8 h-8 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                <TranslatedText>{complaint.name}</TranslatedText>
              </h3>

            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">
                  {t('dashboard.table.complaintId')}
                </label>
                <p className="text-sm font-medium text-gray-900">
                  {complaint.complaintId}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500">
                  {t('track.details.mobile')}
                </label>
                <p className="text-sm text-gray-900">{formatNumberForLanguage(complaint.mobile, currentLanguage)}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500">
                  {t('dashboard.modal.location')}
                </label>
                <p className="text-sm text-gray-900"><TranslatedText>{complaint.village}</TranslatedText></p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500">
                  <TranslatedText>Date Submitted</TranslatedText>
                </label>
                <p className="text-sm text-gray-900">
                  {formatNumberForLanguage(complaint.submittedDate, currentLanguage)}
                </p>
              </div>

              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-500">
                  {t('dashboard.filter.priority')}:
                </label>
                <span
                  className={`inline-flex px-3 py-1.5 text-xs font-semibold rounded-full border
      ${complaint.priority === 'HIGH'
                      ? 'text-red-600 border-red-400 bg-red-50'
                      : complaint.priority === 'LOW'
                        ? 'text-green-600 border-green-400 bg-green-50'
                        : complaint.priority === 'MEDIUM'
                          ? 'text-yellow-600 border-yellow-400 bg-yellow-50'
                          : 'text-gray-600 border-gray-300 bg-gray-50'
                    }`}
                >
                  <TranslatedText>{complaint.priority}</TranslatedText>
                </span>
              </div>



              <div>
                <label className="text-sm font-medium text-gray-500">
                  {t('complaint.currentStatus')}
                </label>
                <p className="text-sm text-gray-900"><TranslatedText>{complaint.status || 'unassign'}</TranslatedText></p>
              </div>
            </div>
          </div>

          {/* Main Content - Assignment Details */}
          <div className="flex-1 p-4 space-y-4 md:space-y-6 md:p-6 word-wrap-300">
            {/* Complaint Description */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-4 py-4 border-b border-gray-200 md:px-6">
                <h3 className="text-lg font-semibold text-gray-900 text-wrap">
                  {t('complaint.form.description')}                </h3>
              </div>
              <div className="p-4 md:p-6">
                <div className="space-y-4 overflow-y-auto w-600 lg:w-4/4 max-h-64 md:w-full">
                  <TranslatedText
                    tag="p"
                    className="text-gray-700 break-all text-wrap"
                  >
                    {complaint.description}
                  </TranslatedText>
                </div>
              </div>
            </div>

            {/* Assignment Form */}
            <div className="bg-white border-l-4 border-blue-500 rounded-lg shadow">
              <div className="px-4 py-4 border-b border-gray-200 md:px-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('assign.title')}
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                  {t('assign.subtitle')}
                </p>
              </div>
              <div className="p-4 md:p-6">
                <div className="space-y-4">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">
                        {t('complaint.form.Department')}
                      </label>
                      <select
                        name="department"
                        value={assignee}
                        onChange={(e) => {
                          setAssignee(e.target.value);
                          if (errors.department) {
                            setErrors({ ...errors, department: null });
                          }
                        }}
                        required
                        className={`w-full px-3 py-3 text-sm text-gray-900 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.department ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-transparent'
                          }`}
                      >
                        <option value="">{t('assign.selectDepartment')}</option>
                        {Array.isArray(departments) &&
                          departments
                            .filter((dept) => dept.name !== departmentName)
                            .map((dept) => (
                              <option key={dept.id} value={dept.id}>
                                <TranslatedText>{dept.name}</TranslatedText>
                              </option>
                            ))}
                      </select>
                      {errors.department && (
                        <p className="mt-1 text-xs text-red-500">{t(errors.department)}</p>
                      )}
                    </div>
                    <div>
                      {/* <label className="block mb-2 text-sm font-medium text-gray-700">Officer</label> */}
                      {/* <select
                        name="officer"
                        value={officer}
                        onChange={(e) => setOfficer(e.target.value)}
                        onFocus={loadOfficers}
                        required
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                      >
                        <option value="">Select Officer</option>
                        {Array.isArray(officers) && officers.map((off) => (
                          <option key={off.id} value={off.id}>
                            {off.name}
                          </option>
                        ))}
                      </select> */}
                    </div>
                  </div>

                  {/* <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">Department</label>
                    <select
                      name="department"
                      value={assignee}
                      onChange={(e) => setAssignee(e.target.value)}
                      required
                      className="px-3 py-3 text-sm text-gray-900 border border-gray-300 rounded-lg focus:border-transparent focus:ring-2 focus:ring-blue-500"
                      style={{ width: "650px" }}
                    >
                      <option value="">Select Department</option>
                      {Array.isArray(departments) &&
                        departments
                          .filter((dept) => dept.name !== "Collector & DM")
                          .map((dept) => (
                            <option key={dept.id} value={dept.id}>
                              {dept.name}
                            </option>
                          ))}
                    </select>

                  </div> */}

                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      {t('assign.specialInstructions')}
                    </label>
                    <textarea
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      className="w-full px-3 py-3 text-sm border border-gray-300 rounded-lg resize-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                      rows="3"
                      placeholder={t('assign.specialInstructionsPlaceholder')}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Professional Action Bar */}
        <div className="flex flex-col items-center justify-between gap-4 px-4 py-4 border-t border-gray-200 bg-gray-50 sm:flex-row md:px-6">
          <div className="text-sm text-center text-gray-600 sm:text-left">
            {t('assign.info')}
          </div>
          <div className="flex w-full gap-3 sm:w-auto">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-2 text-sm font-medium text-gray-700 transition-colors border border-gray-300 rounded-lg hover:bg-gray-50 sm:flex-none"
            >
              {t('track.Cancel')}
            </button>
            <button
              onClick={handleAssign}
              className="flex-1 px-6 py-2 text-sm font-medium text-white transition-colors bg-blue-600 rounded-lg hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 sm:flex-none"
            // disabled={!assignee || !officer}
            >
              {t('assign.button')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
export default AssignComplaintPage;
